import React, { useState } from 'react';
import { Send, MessageSquare, Building, User, Flag, Tag, FileText, Star, Info } from 'lucide-react';
import axios from 'axios';
import './feedback.css'
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';

const ClientBloomFeedbackForm = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    yourName: '',
    feedbackType: 'General Feedback',
    urgencyLevel: 'Low - General feedback',
    category: 'Usability/UX',
    briefSummary: '',
    detailedDescription: '',
    suggestedImprovement: '',
    overallRating: 5
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const workspaceId = Auth.getWorkspaceId();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    const payload = {
        ...formData,
        workspaceId
      };

      await axios.post(`${API_URL}/utils/feedback`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

    setIsSubmitted(true);
  } catch (error) {
    console.error("Error submitting feedback:", error.response?.data || error.message);
    alert("There was an issue submitting the form. Please try again.");
  }
};

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Send className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your feedback has been submitted successfully. We truly appreciate you taking the time to help us improve ClientBloom.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
              <div className="text-left">
                <p className="text-blue-800 text-sm font-medium">Your feedback matters!</p>
                <p className="text-blue-700 text-sm">
                  All feedback is reviewed by our development team and helps shape ClientBloom's future. Critical issues are addressed within 24-48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="form-scroll-container">
      <div className="px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="w-16 h-10 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg flex items-center justify-center">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div className="ml-3">
            <h1 className="text-2xl font-bold text-gray-900">Client Feedback Form</h1>
            <p className="text-gray-600">Help us improve ClientBloom by sharing your experience, suggestions, and any issues you've encountered.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Name */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Building className="h-4 w-4 mr-2" />
            Company Name *
          </label>
          <input
            type="text"
            required
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            className="w-full box-border p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter company name"
          />
        </div>

        {/* Your Name */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <User className="h-4 w-4 mr-2" />
            Your Name *
          </label>
          <input
            type="text"
            required
            value={formData.yourName}
            onChange={(e) => handleInputChange('yourName', e.target.value)}
            className="w-full box-border p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your name"
          />
        </div>

        {/* Feedback Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Feedback Type *
          </label>
          <select
            required
            value={formData.feedbackType}
            onChange={(e) => handleInputChange('feedbackType', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="General Feedback">General Feedback</option>
            <option value="Bug Report">Bug Report</option>
            <option value="Feature Request">Feature Request</option>
            <option value="Usability Issue">Usability Issue</option>
            <option value="Performance Issue">Performance Issue</option>
            <option value="Integration Issue">Integration Issue</option>
            <option value="Compliment">Compliment</option>
            <option value="Complaint">Complaint</option>
          </select>
        </div>

        {/* Urgency Level */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Flag className="h-4 w-4 mr-2" />
            Urgency Level *
          </label>
          <select
            required
            value={formData.urgencyLevel}
            onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Low - General feedback">Low - General feedback</option>
            <option value="Medium - Minor issue affecting workflow">Medium - Minor issue affecting workflow</option>
            <option value="High - Major issue blocking tasks">High - Major issue blocking tasks</option>
            <option value="Critical - System down or data loss">Critical - System down or data loss</option>
          </select>
        </div>

        {/* Category */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Tag className="h-4 w-4 mr-2" />
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Usability/UX">Usability/UX</option>
            <option value="Performance">Performance</option>
            <option value="Features">Features</option>
            <option value="Integrations">Integrations</option>
            <option value="Data/Analytics">Data/Analytics</option>
            <option value="Mobile Experience">Mobile Experience</option>
            <option value="Security">Security</option>
            <option value="Billing/Account">Billing/Account</option>
            <option value="Documentation">Documentation</option>
            <option value="Support">Support</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Brief Summary */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brief Summary *
          </label>
          <input
            type="text"
            required
            value={formData.briefSummary}
            onChange={(e) => handleInputChange('briefSummary', e.target.value)}
            className="w-full box-border p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="One-line description of the feedback"
          />
        </div>

        {/* Detailed Description */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <FileText className="h-4 w-4 mr-2" />
            Detailed Description *
          </label>
          <textarea
            required
            value={formData.detailedDescription}
            onChange={(e) => handleInputChange('detailedDescription', e.target.value)}
            className="w-full box-border p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            placeholder="Please provide specific details about your experience, including steps to reproduce any issues..."
          />
        </div>

        {/* Suggested Improvement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Suggested Improvement (Optional)
          </label>
          <textarea
            value={formData.suggestedImprovement}
            onChange={(e) => handleInputChange('suggestedImprovement', e.target.value)}
            className="w-full box-border p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
            placeholder="How would you like to see this improved or what would make this better?"
          />
        </div>

        {/* Overall Experience Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Overall Experience Rating (1-10)
          </label>
          <div className="relative">
            <input
              type="range"
              min="1"
              max="10"
              value={formData.overallRating}
              onChange={(e) => handleInputChange('overallRating', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(formData.overallRating - 1) * 11.11}%, #e5e7eb ${(formData.overallRating - 1) * 11.11}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Poor</span>
              <span className="text-lg font-bold text-purple-600">{formData.overallRating}</span>
              <span>Excellent</span>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-3 px-6 rounded-md hover:from-purple-700 hover:to-pink-600 transition-all duration-200 flex items-center justify-center font-medium cursor-pointer"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Feedback
          </button>
        </div>
      </form>

      {/* Footer Message */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <p className="text-blue-800 text-sm font-medium">Your feedback matters!</p>
            <p className="text-blue-700 text-sm">
              All feedback is reviewed by our development team and helps shape ClientBloom's future. Critical issues are addressed within 24-48 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  );
};

export default ClientBloomFeedbackForm;